import React, { useState } from "react";

import { Panel, Pill } from "./parts";
import { S } from "./strings";
import {
  PlanTemplate,
  TemplateSection,
} from "../../Store/Reducers/AccountPlanTemplateSlice";

type Persona = "ES" | "AE" | "CSM";

const PERSONAS: { key: Persona; label: string }[] = [
  { key: "ES", label: S.personaSponsor },
  { key: "AE", label: S.personaAe },
  { key: "CSM", label: S.personaCsm },
];

// The CSM view puts Risks & escalations straight after the header (SponsorViewTab.tsx).
const RISKS_KEY = "s8";

const ordered = (
  sections: TemplateSection[],
  persona: Persona
): TemplateSection[] => {
  if (persona !== "CSM") return sections;
  const risks = sections.find(s => s.key === RISKS_KEY);
  if (!risks || sections[0] === risks) return sections;
  const rest = sections.filter(s => s !== risks);
  return [rest[0], risks].concat(rest.slice(1));
};

const status = (s: TemplateSection, persona: Persona): string => {
  if (!s.enabled) return S.pvHidden;
  if (s.system) return S.pvSystem;
  if (persona === "CSM" && s.collapsed_for_csm) return S.pvCollapsed;
  return s.custom ? S.pvCustom : S.pvCited;
};

const BriefingPreview: React.FC<{ template: PlanTemplate }> = ({
  template,
}) => {
  const [persona, setPersona] = useState<Persona>("ES");
  const questions = template.questions.filter(q => q.enabled);

  return (
    <Panel
      title={S.previewTitle}
      hint={S.previewHint}
      className="lg:sticky lg:top-4"
      right={
        <div
          role="radiogroup"
          aria-label={S.previewTitle}
          className="flex gap-0.5 rounded-md bg-gray-100 p-0.5"
        >
          {PERSONAS.map(p => (
            <button
              key={p.key}
              type="button"
              role="radio"
              aria-checked={persona === p.key}
              onClick={() => setPersona(p.key)}
              className={`rounded px-2.5 py-0.5 text-xs font-medium ${
                persona === p.key
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      }
    >
      <div className="grid gap-4 p-4">
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            {S.briefing}
          </p>
          <ul className="m-0 grid list-none gap-2 p-0">
            {ordered(template.sections, persona).map(s => {
              const yours =
                persona !== "ES" && s.enabled && s.owner === persona;
              return (
                <li
                  key={s.key}
                  className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 ${
                    s.enabled
                      ? "border-gray-200"
                      : "border-dashed border-gray-300 opacity-50"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block break-words text-sm font-semibold text-gray-900">
                      {s.title || S.untitledSection}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {status(s, persona)}
                    </span>
                  </span>
                  <span className="flex shrink-0 gap-1">
                    {yours && <Pill tone="amber">{S.youVerify}</Pill>}
                    {s.custom && <Pill tone="blue">{S.custom}</Pill>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            {S.qaBreakdown}
          </p>
          <ol className="m-0 list-none p-0">
            {questions.map((q, i) => (
              <li
                key={q.key}
                className="grid grid-cols-[28px_minmax(0,1fr)] gap-1 border-t border-gray-100 py-1.5 text-sm text-gray-800 first:border-t-0"
              >
                <span className="font-mono text-xs tabular-nums text-gray-400">
                  Q{i + 1}
                </span>
                <span className="break-words">
                  {q.text || S.untitledQuestion}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </Panel>
  );
};

export default BriefingPreview;
